import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Actions from '@/app/store/actions';
import { Typography, Avatar, CircularProgress, Tooltip } from '@material-ui/core';
import CustomButton from '@/app/customComponents/CustomButton/CustomButton';
import { uploadFile } from '@/app/services/imageUpload/imageUpload';
import '../parentAnnouncement/parentAnnouncement.css';
import { getImageUrl } from '@/utils/utils';
import PageHeader from '@/app/customComponents/PageHeader';
import { Close } from '@material-ui/icons';
import { useNavigate } from 'react-router';
import { sendMessageToEntireRoom } from '@/app/services/messages/messages';
import StyledInput from 'shared/components/StyledInput';
import { DocumentIcon } from '@heroicons/react/24/outline';

const AnnouncementForm = ({ selectedRooms, setActiveTab, setSelectedRooms }) => {
	const ref = useRef(null);
	const dispatch = useDispatch();
	const [form, setForm] = useState({});
	const [errTxts, setErrTxts] = useState({});
	const [selectedFile, setSelectedFile] = useState(null);
	const [preview, setPreview] = useState(null);
	const roomsToShowWithIcons = selectedRooms.length > 2 ? selectedRooms.slice(0, 2) : selectedRooms;
	const [isLoading, setLoading] = useState(false);
	const navigate = useNavigate();

	useEffect(() => {
		if (!selectedFile) {
			setPreview(null);
			return;
		}
		const objectUrl = URL.createObjectURL(selectedFile.file);
		setPreview(objectUrl);
		return () => URL.revokeObjectURL(objectUrl);
	}, [selectedFile]);

	function handleChange({ target }) {
		const { name, value } = target;
		setErrTxts({});
		setForm({ ...form, [name]: value });
	}

	function handleFileUpload(e) {
		const file = e.target.files[0];
		if (!file) {
			return;
		}

		const fileSize = 0.000001 * file.size;
		if (fileSize > 25) {
			dispatch(
				Actions.showMessage({
					message: 'File size cannot exceed 25 MBs',
					autoHideDuration: 2500,
					variant: 'error',
				})
			);
			return;
		}

		// Determine file type category
		let fileType = 'file';
		if (file.type.startsWith('image/')) {
			fileType = 'image';
		} else if (file.type.startsWith('video/')) {
			fileType = 'video';
		} else if (file.type.startsWith('audio/')) {
			fileType = 'audio';
		}

		setSelectedFile({
			file,
			fileType,
			meta: {
				type: fileType,
				filename: file.name,
				filesize: file.size,
				mime: file.type || 'application/octet-stream', // Fallback mime type if none detected
				fileExtension: file.name.split('.').pop().toLowerCase(),
			},
		});
	}

	function addform() {
		setErrTxts({});

		if (!form.message) {
			setErrTxts({ ...errTxts, message: 'This field is required.' });
			return;
		}
		if (form.message.length > 1000) {
			setErrTxts({ ...errTxts, message: 'Announcement cannot be more than 1000 characters long!' });
			return;
		}

		if (!selectedRooms.length) {
			dispatch(
				Actions.showMessage({
					message: 'Please select at least one room',
					autoHideDuration: 2500,
					variant: 'error',
				})
			);
			return;
		}

		const room_id = selectedRooms.map((room) => room.id);

		setLoading(true);
		if (selectedFile) {
			const filename = getImageUrl(selectedFile.file);
			const _URL = window.URL || window.webkitURL;

			if (selectedFile.fileType === 'image') {
				const img = new Image();
				const objectUrl = _URL.createObjectURL(selectedFile.file);

				img.onload = () => {
					_URL.revokeObjectURL(objectUrl);

					uploadFile(selectedFile.file, filename)
						.then((response) => {
							const attachment = `${import.meta.env.VITE_S3_BASE_URL}${response}`;
							const payload = {
								room_id,
								message: form.message,
								attachment,
								meta: {
									type: 'image',
									filename: selectedFile.meta.filename,
									filesize: selectedFile.meta.filesize,
									mime: selectedFile.meta.mime,
									width: img.width,
									height: img.height,
								},
							};

							sendMessageToEntireRoom(payload)
								.then((res) => {
									handleSuccess(res);
								})
								.catch((err) => {
									handleError(err);
								});
						})
						.catch((err) => {
							handleError(err);
						});
				};

				img.src = objectUrl;
			} else {
				uploadFile(selectedFile.file, filename)
					.then((response) => {
						const attachment = `${import.meta.env.VITE_S3_BASE_URL}${response}`;
						const payload = {
							room_id,
							message: form.message,
							attachment,
							meta: {
								type: selectedFile.fileType, // audio, video, or file
								filename: selectedFile.meta.filename,
								filesize: selectedFile.meta.filesize,
								mime: selectedFile.meta.mime,
							},
						};

						sendMessageToEntireRoom(payload)
							.then((res) => {
								handleSuccess(res);
							})
							.catch((err) => {
								handleError(err);
							});
					})
					.catch((err) => {
						handleError(err);
					});
			}
		} else {
			const payload = {
				room_id,
				message: form.message,
			};

			sendMessageToEntireRoom(payload)
				.then((res) => {
					handleSuccess(res);
				})
				.catch((err) => {
					handleError(err);
				});
		}
	}

	function handleSuccess(res) {
		dispatch(
			Actions.showMessage({
				message: res.data.message,
				autoHideDuration: 2500,
				variant: 'success',
			})
		);
		setLoading(false);
		setSelectedRooms([]);
		setActiveTab(0);
	}

	function handleError(err) {
		if (err.response?.data?.message) {
			dispatch(
				Actions.showMessage({
					message: err.response.data.message,
					autoHideDuration: 2500,
					variant: 'error',
				})
			);
		} else {
			dispatch(
				Actions.showMessage({
					message: 'Failed to post message',
					autoHideDuration: 2500,
					variant: 'error',
				})
			);
		}
	}

	const handleGoBack = () => {
		navigate('/messaging/announcements/room');
		setActiveTab();
	};

	return (
		<div className="mt-20 w-11/12 mx-auto">
			<PageHeader onBack={handleGoBack} title="Message" />
			<div className="bg-white rounded mt-12 px-32 sm:px-60 pt-32 pb-32">
				<div className="flex flex-col sm:items-center sm:flex-row ">
					<h2 className="font-bold" style={{ paddingRight: '8px', fontSize: '18px' }}>
						Recipients:
					</h2>
					<div className="flex flex-row items-center">
						{roomsToShowWithIcons.map((room, key) => {
							const isLast = key === roomsToShowWithIcons.length - 1;
							let remainingRooms = '';
							if (selectedRooms.length > 2) {
								for (let i = 2; i < selectedRooms.length; i++) {
									remainingRooms += selectedRooms[i].name + ', ';
								}
								remainingRooms = remainingRooms.slice(0, -2);
							}

							return (
								<Fragment key={key}>
									<Tooltip key={key} title={room?.name}>
										<Avatar
											src={room.thumb}
											style={{
												padding: '0px',
												width: '33px',
												height: '33px',
												marginRight: '2px',
											}}
										/>
									</Tooltip>
									{isLast && selectedRooms.length > 2 && selectedRooms.length > 2 ? (
										<Tooltip title={remainingRooms}>
											<span className="font-bold" style={{ marginLeft: '6px' }}>{`${' '} ${
												selectedRooms.length - 2
											} other rooms`}</span>
										</Tooltip>
									) : null}
								</Fragment>
							);
						})}
					</div>
				</div>
				<div className="mt-48 max-w-640">
					<StyledInput
						label="Type message"
						multiline
						name="message"
						onChange={handleChange}
						value={form.message}
						fullWidth
						rows={4}
						className="mt-20"
						helperText={errTxts.message}
						error={errTxts.message}
						required
					/>
				</div>

				<div className="mt-20 relative">
					<Typography className="file-upload-label">Upload File</Typography>
					{preview && selectedFile ? (
						<div className="relative inline-block">
							{selectedFile.fileType === 'image' ? (
								<>
									<img alt="" src={preview} style={{ width: '87px', height: '84px', objectFit: 'cover' }} />
									<button className="cross cross-announcement-img" onClick={() => setSelectedFile(null)} type="button">
										<Close className="size-12" />
									</button>
								</>
							) : (
								<div className="relative flex flex-col items-center" style={{ width: '87px' }}>
									<DocumentIcon className="size-60 text-pd-blue" />
									<div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 translate-y-[-2px]">
										<Typography className="text-10 uppercase font-bold text-pd-blue">
											{selectedFile.meta.fileExtension}
										</Typography>
									</div>
									<button className="cross cross-announcement-pdf" onClick={() => setSelectedFile(null)} type="button">
										<Close className="size-12" />
									</button>
								</div>
							)}
							<Typography className="mt-8 text-12" style={{ maxWidth: '200px', wordBreak: 'break-word' }}>
								{selectedFile.meta.filename}
							</Typography>
						</div>
					) : (
						<>
							<button onClick={() => ref.current.click()} type="button">
								<img alt="Upload file" src="assets/images/addFile.png" style={{ cursor: 'pointer' }} />
							</button>
							<input
								accept="*/*"
								name="file"
								onChange={handleFileUpload}
								ref={ref}
								style={{ display: 'none' }}
								type="file"
							/>
						</>
					)}
				</div>

				{!isLoading ? (
					<div className="flex justify-center mt-20 items-center w-full flex-wrap gap-16">
						<CustomButton minWidth={150} onClick={handleGoBack} variant="secondary">
							Cancel
						</CustomButton>

						<CustomButton minWidth={150} onClick={addform} variant="primary">
							Send Message
						</CustomButton>
					</div>
				) : (
					<div className="flex justify-center my-24">
						<CircularProgress className="mx-auto" />
					</div>
				)}
			</div>
		</div>
	);
};

export default AnnouncementForm;
